import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, fmtDate, fileUrl, formatApiError, priorityColors, statusColors } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Star, Image as ImageIcon, CheckSquare, Wrench, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export function ApartmentQuickActions({ apartmentId, onDone, onGoDocs }) {
  const [dialog, setDialog] = useState(null);
  const [task, setTask] = useState({ title: "", due_date: "", priority: "media", notes: "" });
  const [maint, setMaint] = useState({ description: "", priority: "media", notes: "" });
  const [photo, setPhoto] = useState({ file: null, description: "" });
  const [busy, setBusy] = useState(false);

  const saveTask = async () => {
    if (!task.title) return toast.error("Titolo obbligatorio");
    await http.post("/tasks", { ...task, apartment_id: apartmentId, due_date: task.due_date || null, status: "da_fare" });
    toast.success("Todo creato"); setDialog(null); setTask({ title: "", due_date: "", priority: "media", notes: "" }); onDone?.();
  };
  const saveMaint = async () => {
    if (!maint.description) return toast.error("Descrizione obbligatoria");
    await http.post("/maintenance", { ...maint, apartment_id: apartmentId, reported_date: today(), status: "da_fare",
      charged_to: "proprietario", considera_rendiconto: true, mostra_pdf: true, photo_before_ids: [], photo_after_ids: [] });
    toast.success("Manutenzione creata"); setDialog(null); setMaint({ description: "", priority: "media", notes: "" }); onDone?.();
  };
  const savePhoto = async () => {
    if (!photo.file) return toast.error("Seleziona una foto");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", photo.file); fd.append("description", photo.description || "");
      await http.post(`/apartments/${apartmentId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto caricata"); setDialog(null); setPhoto({ file: null, description: "" }); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid="apt-quick-actions">
        <Button size="sm" variant="outline" className="rounded-full" data-testid="apt-add-todo" onClick={() => setDialog("task")}><CheckSquare className="h-4 w-4 mr-1" /> Todo</Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="apt-add-maint" onClick={() => setDialog("maint")}><Wrench className="h-4 w-4 mr-1" /> Manutenzione</Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="apt-add-photo" onClick={() => setDialog("photo")}><ImageIcon className="h-4 w-4 mr-1" /> Foto</Button>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="apt-add-doc" onClick={() => onGoDocs?.()}><FolderOpen className="h-4 w-4 mr-1" /> Documento</Button>
      </div>

      <Dialog open={dialog === "task"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="apt-task-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo Todo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Attività</Label><Input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} data-testid="apt-task-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Scadenza</Label><Input type="date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} /></div>
              <div><Label>Priorità</Label>
                <Select value={task.priority} onValueChange={(v) => setTask({ ...task, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["bassa", "media", "alta", "urgente"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={task.notes} onChange={(e) => setTask({ ...task, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button><Button onClick={saveTask} className="bg-[var(--teal-deep)]" data-testid="apt-task-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "maint"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="apt-maint-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuova manutenzione</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descrizione</Label><Input value={maint.description} onChange={(e) => setMaint({ ...maint, description: e.target.value })} data-testid="apt-maint-desc" /></div>
            <div><Label>Priorità</Label>
              <Select value={maint.priority} onValueChange={(v) => setMaint({ ...maint, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["bassa", "media", "alta", "urgente"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={maint.notes} onChange={(e) => setMaint({ ...maint, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button><Button onClick={saveMaint} className="bg-[var(--teal-deep)]" data-testid="apt-maint-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "photo"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="apt-photo-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Carica foto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Foto</Label><Input type="file" accept="image/*" onChange={(e) => setPhoto({ ...photo, file: e.target.files?.[0] || null })} data-testid="apt-photo-file" /></div>
            <div><Label>Descrizione</Label><Input value={photo.description} onChange={(e) => setPhoto({ ...photo, description: e.target.value })} data-testid="apt-photo-desc" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(null)}>Annulla</Button><Button onClick={savePhoto} disabled={busy} className="bg-[var(--teal-deep)]" data-testid="apt-photo-save">{busy ? "Carico..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function OverviewPanel({ ov }) {
  const a = ov.apartment;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="apt-overview">
      <Card className="rounded-2xl overflow-hidden lg:col-span-1">
        {ov.cover_photo ? <img src={fileUrl(ov.cover_photo.file_id)} alt="" className="h-44 w-full object-cover" data-testid="apt-cover" /> :
          <div className="h-44 w-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon className="h-8 w-8" /></div>}
        <div className="p-4 space-y-1 text-sm">
          <div className="font-display font-bold text-[var(--teal-deep)]">{a.name}</div>
          <div className="text-slate-500 text-xs">{a.address || "-"}{a.city ? ` · ${a.city}` : ""}</div>
          <div className="text-slate-600">Proprietario: <b>{ov.owner?.name || "—"}</b></div>
          <div className="text-slate-600 text-xs">Capienza {a.capacity} · {a.bathrooms} bagni · gestione {a.management_percent}%</div>
          {a.notes && <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">{a.notes}</div>}
        </div>
      </Card>

      <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="rounded-2xl p-4" data-testid="ov-todo-open"><div className="text-[10px] uppercase text-slate-500 font-semibold">Todo aperti</div><div className="font-display text-2xl font-extrabold">{ov.tasks.open.length}</div><div className="text-[11px] text-rose-600">{ov.tasks.overdue.length} scaduti</div></Card>
        <Card className="rounded-2xl p-4" data-testid="ov-maint-open"><div className="text-[10px] uppercase text-slate-500 font-semibold">Manutenzioni aperte</div><div className="font-display text-2xl font-extrabold">{ov.maintenance.open.length}</div><div className="text-[11px] text-slate-500">{ov.maintenance.total} totali</div></Card>
        <Card className="rounded-2xl p-4" data-testid="ov-docs"><div className="text-[10px] uppercase text-slate-500 font-semibold">Documenti</div><div className="font-display text-2xl font-extrabold">{ov.documents.length}</div><div className="text-[11px] text-amber-700">{ov.documents.filter((d) => d.expiry_state === "in_scadenza").length} in scadenza · {ov.documents.filter((d) => d.expiry_state === "scaduto").length} scaduti</div></Card>
        <Card className="rounded-2xl p-4" data-testid="ov-reviews"><div className="text-[10px] uppercase text-slate-500 font-semibold">Recensioni</div><div className="font-display text-2xl font-extrabold flex items-center gap-1">{ov.reviews.count}{ov.reviews.avg_rating ? <span className="text-sm text-slate-500">· {ov.reviews.avg_rating}<Star className="h-3 w-3 inline text-amber-500" /></span> : null}</div></Card>

        <Card className="rounded-2xl p-4 col-span-2">
          <div className="font-display font-bold text-[var(--teal-deep)] text-sm mb-2">Todo urgenti / scaduti</div>
          {[...ov.tasks.overdue, ...ov.tasks.open.filter((t) => t.priority === "urgente")].slice(0, 4).map((t) => (
            <div key={t.id} className="text-sm flex items-center gap-2 py-1 border-b border-slate-100 last:border-0">
              <span className="flex-1 truncate">{t.title}</span>
              {t.due_date && <span className="text-[11px] text-slate-500">{fmtDate(t.due_date)}</span>}
              <Badge variant="outline" className={`${priorityColors[t.priority]} text-[10px]`}>{t.priority}</Badge>
            </div>
          ))}
          {ov.tasks.overdue.length === 0 && ov.tasks.open.filter((t) => t.priority === "urgente").length === 0 && <div className="text-xs text-slate-400">Nulla di urgente</div>}
        </Card>
        <Card className="rounded-2xl p-4 col-span-2">
          <div className="font-display font-bold text-[var(--teal-deep)] text-sm mb-2">Problemi dalle recensioni</div>
          <div className="flex flex-wrap gap-2">
            {ov.reviews.problems.slice(0, 6).map((p) => <Badge key={p.category} variant="outline" className="border-rose-200 text-rose-700">{p.category} · {p.count}</Badge>)}
            {ov.reviews.problems.length === 0 && <span className="text-xs text-slate-400">Nessun problema rilevato</span>}
          </div>
          <Link to="/recensioni" className="text-[11px] text-[var(--teal-deep)] underline mt-2 inline-block">Analisi recensioni →</Link>
        </Card>
      </div>
    </div>
  );
}

export function TodoPanel({ ov, reload }) {
  const complete = async (t) => { await http.put(`/tasks/${t.id}`, { ...t, status: "completato" }); reload(); };
  const list = (items, label, testid) => (
    <Card className="rounded-2xl p-4" data-testid={testid}>
      <div className="font-display font-bold text-[var(--teal-deep)] text-sm mb-2">{label} ({items.length})</div>
      {items.length === 0 && <div className="text-xs text-slate-400">Nessuno</div>}
      {items.map((t) => (
        <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0 text-sm" data-testid={`apt-task-${t.id}`}>
          <span className="flex-1 min-w-0"><span className="truncate">{t.title}</span>
            {t.source_label && <span className="text-[11px] text-[var(--teal-deep)] ml-2">({t.source_label})</span>}</span>
          {t.due_date && <span className="text-[11px] text-slate-500">{fmtDate(t.due_date)}</span>}
          <Badge variant="outline" className={`${statusColors[t.status]} text-[10px]`}>{t.status.replace("_", " ")}</Badge>
          {t.status !== "completato" && <button onClick={() => complete(t)} className="text-xs text-emerald-700 underline" data-testid={`apt-task-complete-${t.id}`}>completa</button>}
        </div>
      ))}
    </Card>
  );
  return (
    <div className="space-y-4">
      {list(ov.tasks.open, "Aperti", "apt-todo-open")}
      {list(ov.tasks.overdue, "Scaduti", "apt-todo-overdue")}
      {list(ov.tasks.done_recent, "Completati recenti", "apt-todo-done")}
    </div>
  );
}

export function PhotosPanel({ apartmentId, ov, reload }) {
  const [photos, setPhotos] = useState(ov.photos || []);
  useEffect(() => { setPhotos(ov.photos || []); }, [ov]);
  const setCover = async (p) => { await http.patch(`/photos/${p.id}`, { is_cover: true }); toast.success("Copertina aggiornata"); reload(); };
  const del = async (p) => { if (!window.confirm("Eliminare la foto?")) return; await http.delete(`/photos/${p.id}`); reload(); };
  const setDesc = async (p, description) => { await http.patch(`/photos/${p.id}`, { description }); reload(); };
  return (
    <div className="space-y-3" data-testid="apt-photos">
      <div className="text-xs text-slate-500">Le foto restano nell'archivio dell'appartamento e sono riutilizzabili nei preventivi senza ricaricarle.</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.length === 0 && <div className="col-span-full text-center text-slate-400 py-8">Nessuna foto.</div>}
        {photos.map((p) => (
          <Card key={p.id} className="rounded-2xl overflow-hidden" data-testid={`apt-photo-${p.id}`}>
            <img src={fileUrl(p.file_id)} alt={p.description || p.filename} className="h-32 w-full object-cover" />
            <div className="p-2 space-y-1">
              <Input defaultValue={p.description || ""} placeholder="Descrizione" className="h-8 text-xs"
                onBlur={(e) => e.target.value !== (p.description || "") && setDesc(p, e.target.value)} data-testid={`apt-photo-desc-${p.id}`} />
              <div className="flex items-center gap-2">
                {p.is_cover ? <Badge className="bg-teal-100 text-teal-800 text-[10px]">COPERTINA</Badge> :
                  <button onClick={() => setCover(p)} className="text-[11px] text-[var(--teal-deep)] underline" data-testid={`apt-photo-cover-${p.id}`}>Imposta copertina</button>}
                <button onClick={() => del(p)} className="ml-auto p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function ReviewsPanel({ ov }) {
  const [orig, setOrig] = useState({});
  return (
    <div className="space-y-3" data-testid="apt-reviews">
      <div className="flex flex-wrap gap-2">
        {ov.reviews.problems.map((p) => <Badge key={p.category} variant="outline" className="border-rose-200 text-rose-700">{p.category} · {p.count}</Badge>)}
        {ov.reviews.problems.length === 0 && <span className="text-xs text-slate-400">Nessun problema ricorrente rilevato</span>}
      </div>
      <Card className="rounded-2xl p-4">
        {ov.reviews.recent.length === 0 && <div className="text-sm text-slate-400">Nessuna recensione collegata.</div>}
        {ov.reviews.recent.map((r) => (
          <div key={r.id} className="py-2 border-b border-slate-100 last:border-0" data-testid={`apt-review-${r.id}`}>
            <div className="text-xs text-slate-500 flex gap-2 items-center"><Badge variant="outline" className="text-[10px]">{r.platform}</Badge>{fmtDate(r.date)}{r.rating != null && <span>voto {r.rating}</span>}</div>
            <div className="text-sm text-slate-700">
              {orig[r.id] || r.translation_status !== "tradotta"
                ? (r.text || [r.positive_text, r.negative_text].filter(Boolean).join(" — "))
                : (r.text_it || [r.positive_text_it, r.negative_text_it].filter(Boolean).join(" — "))}
            </div>
            {r.translation_status === "tradotta" && (
              <button onClick={() => setOrig({ ...orig, [r.id]: !orig[r.id] })} className="text-[11px] text-[var(--teal-deep)] underline" data-testid={`apt-review-toggle-${r.id}`}>
                {orig[r.id] ? "Vedi traduzione italiana" : "Vedi originale"}
              </button>
            )}
            {r.translation_status === "non_disponibile" && <span className="text-[11px] text-amber-700">Traduzione non disponibile</span>}
          </div>
        ))}
      </Card>
      <Link to="/recensioni" className="text-sm text-[var(--teal-deep)] underline">Apri Analisi recensioni →</Link>
    </div>
  );
}
